import React from 'react';
import PropTypes from 'prop-types';
import {
    DndContext,
    DragOverlay,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragStartEvent,
    DragEndEvent,
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

type Item = {
    id: number;
};

interface SortableItemProps<T extends Item> {
    item: T;
    index: number;
    children: (item: T, index: number, isDragging: boolean) => React.ReactNode;
    isDragging: boolean;
}

function SortableItem<T extends Item>({
    item,
    index,
    children,
    isDragging,
}: SortableItemProps<T>) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging: isSortableDragging,
    } = useSortable({ id: item.id });

    const baseTransform = CSS.Transform.toString(transform);
    const dragTransform = isSortableDragging ? `${baseTransform} rotate(2deg) scale(1.02)` : baseTransform;
    
    const style = {
        transform: dragTransform,
        transition,
        opacity: isSortableDragging ? 0.9 : 1,
        zIndex: isSortableDragging ? 1000 : 'auto',
        cursor: isSortableDragging ? 'grabbing' : 'grab',
        ...(isSortableDragging && {
            boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
        }),
    };

    return (
        <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
            {children(item, index, isDragging || isSortableDragging)}
        </div>
    );
}

function ImprovedDragAndDropList<T extends Item>(props: Props<T>) {
    const {
        list,
        onDragStart,
        onDragEnd,
        children,
        disabled = false,
    } = props;

    const [activeId, setActiveId] = React.useState<number | null>(null);
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8, // 8px movement required to start drag
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    );

    const handleDragStart = (event: DragStartEvent) => {
        setActiveId(Number(event.active.id));
        if (onDragStart) {
            onDragStart(event);
        }
    };

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (active.id !== over?.id) {
            const oldIndex = list.findIndex((item) => item.id === Number(active.id));
            const newIndex = list.findIndex((item) => item.id === Number(over!.id));

            if (oldIndex !== -1 && newIndex !== -1) {
                const newList = [...list];
                const [movedItem] = newList.splice(oldIndex, 1);
                newList.splice(newIndex, 0, movedItem);
                onDragEnd(newList);
            }
        }

        setActiveId(null);
    };

    const activeItem = activeId ? list.find((item) => item.id === activeId) : null;
    const activeIndex = activeItem ? list.findIndex((item) => item.id === activeId) : -1;

    if (disabled) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                {list.map((item, index) => (
                    <div key={item.id}>
                        {children(item, index, false)}
                    </div>
                ))}
            </div>
        );
    }

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
        >
            <SortableContext items={list.map((item) => item.id)} strategy={verticalListSortingStrategy}>
                <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                    {list.map((item, index) => (
                        <SortableItem
                            key={item.id}
                            item={item}
                            index={index}
                            // eslint-disable-next-line react/no-children-prop
                            children={children}
                            isDragging={activeId === item.id}
                        />
                    ))}
                </div>
            </SortableContext>
            {/* DragOverlay disabled due to positioning issues in modals */}
        </DndContext>
    );
}

ImprovedDragAndDropList.defaultProps = {
    onDragStart: undefined,
    disabled: false,
};

ImprovedDragAndDropList.propTypes = {
    onDragStart: PropTypes.func,
    onDragEnd: PropTypes.func.isRequired,
    children: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
};

type Props<T> = {
    list: T[];
    onDragStart?: (event: DragStartEvent) => void;
    onDragEnd: (items: T[]) => void;
    children: (item: T, index: number, isDragging: boolean) => React.ReactNode;
    disabled?: boolean;
};

export default ImprovedDragAndDropList; 